import React, { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { GateLocation } from '../shared/OurGateTypes';
import { updateDocument } from '../util/FirebaseUtil';
import { parseGateLocation } from '../shared/GateLocation';
import GateLocationMap from './GateLocationMap';

export default function GateLocationEditor({ systemId, location }: { systemId: string; location?: GateLocation | null }) {
  const [latitude, setLatitude] = useState(String(location?.latitude ?? ''));
  const [longitude, setLongitude] = useState(String(location?.longitude ?? ''));
  const [radius, setRadius] = useState(String(location?.radiusMeters ?? 150));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const save = async (clear = false) => {
    setError('');
    setSaved(false);
    try {
      const value = clear ? null : parseGateLocation(latitude, longitude, radius);
      setBusy(true);
      await updateDocument('sites', systemId, { 'config.gateLocation': value });
      if (clear) { setLatitude(''); setLongitude(''); }
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };
  const useCurrentLocation = () => {
    setError('');
    setSaved(false);
    if (!navigator.geolocation) { setError('Location is not available in this browser.'); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      setLatitude(String(coords.latitude));
      setLongitude(String(coords.longitude));
      setBusy(false);
    }, (e) => { setError(e.message); setBusy(false); }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };
  return <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); void save(); }} sx={{ mb: 4 }}>
    <Typography variant="h6">Gate location</Typography>
    <Typography sx={{ mb: 2 }}>Set the gate’s GPS coordinates and arrival radius. Mobile users can enable opening on arrival for this site.</Typography>
    <Stack spacing={2}>
      <Typography variant="body2">Click the map to place the gate. The circle shows the arrival area. Review the coordinates, then choose Save location.</Typography>
      <GateLocationMap latitude={latitude} longitude={longitude} radius={radius} disabled={busy}
        onSelect={(lat, lng) => {
          setLatitude(String(lat));
          setLongitude(String(lng));
          setSaved(false);
          setError('');
        }} />
      <TextField label="Latitude" value={latitude} disabled={busy} required type="number" inputProps={{ min: -90, max: 90, step: 'any' }} onChange={e => { setLatitude(e.target.value); setSaved(false); }} />
      <TextField label="Longitude" value={longitude} disabled={busy} required type="number" inputProps={{ min: -180, max: 180, step: 'any' }} onChange={e => { setLongitude(e.target.value); setSaved(false); }} />
      <TextField label="Arrival radius (meters)" value={radius} disabled={busy} required type="number" inputProps={{ min: 100, max: 1000, step: 'any' }} helperText="100–1,000 meters. Default: 150 meters. Location detection may be delayed." onChange={e => { setRadius(e.target.value); setSaved(false); }} />
      <Stack direction="row" spacing={2}>
        <Button type="submit" variant="contained" disabled={busy}>Save location</Button>
        <Button type="button" onClick={useCurrentLocation} disabled={busy}>Use my current location</Button>
        <Button type="button" onClick={() => void save(true)} disabled={busy}>Remove location</Button>
      </Stack>
      <Typography variant="body2">Use your current location only while standing at the gate. Review the coordinates before saving.</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">Gate location saved.</Alert>}
    </Stack>
  </Box>;
}
